<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import useUiStore from '../../stores/ui';

type Props = {
  isSaving?: boolean;
  sentencePreview?: string;
};
defineProps<Props>();

const { xs, smAndDown } = useDisplay();
const { bottomBarHeight } = storeToRefs(useUiStore());
const minHeight = computed(() => {
  const h = bottomBarHeight.value > 0 ? bottomBarHeight.value : smAndDown.value ? 96 : 64;
  return `${h}px`;
});
</script>

<template>
  <v-card color="inverse-surface" class="compact-bar" tabindex="-1">
    <v-overlay :model-value="isSaving" contained class="align-center justify-center">
      <v-progress-circular indeterminate color="primary" size="32" />
    </v-overlay>

    <div v-if="sentencePreview" class="px-3 pt-2 pb-0">
      <v-chip variant="tonal" density="compact" color="on-inverse-surface" class="sentence-chip text-caption" label>
        {{ sentencePreview }}
      </v-chip>
    </div>

    <template v-if="xs">
      <div class="d-flex align-center justify-center px-2 pt-1">
        <slot name="colors" />
      </div>
      <div class="xs-actions d-flex align-center justify-center">
        <slot name="actions" />
      </div>
    </template>

    <template v-else>
      <div class="sm-row d-flex align-center justify-space-between px-4">
        <slot name="colors" />
        <v-divider vertical class="mx-2" color="outline-variant" />
        <slot name="actions" />
      </div>
    </template>
  </v-card>
</template>

<style scoped lang="scss">
.compact-bar {
  --highlight-ring-color: rgb(var(--v-theme-on-inverse-surface));
  width: 100%;
  min-height: v-bind(minHeight);
  overflow: hidden;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.sentence-chip {
  max-width: 100%;
  overflow: hidden;

  :deep(.v-chip__content) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.xs-actions {
  padding-bottom: max(16px, env(safe-area-inset-bottom));
}

.sm-row {
  height: 100%;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}
</style>
